import React, { useEffect } from "react";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import Select from "react-select";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import apiLink from "../../api/apiLink";
import { useBkash } from "react-bkash";
import customerBillMonth from "../dashboard/customerBillMonth";
import { sslCommerzPayment } from "../../features/customer";
import { toast } from "react-toastify";

import BKashLogo from "../../assets/logo/bKash.png";
import SSLCommerzLogo from "../../assets/logo/sslCommerz.png";
import UddoktaPayLogo from "../../assets/logo/uddoktaPay.png";
import ShurjoPayLogo from "../../assets/logo/shurjoPay.png";

const monthOptions = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((month) => ({ value: month, label: month }));

const Payment = ({ show, setShow, customer }) => {
  const validationSchema = Yup.object().shape({
    amount: Yup.number()
      .required("Amount is required")
      .min(1, "The amount must be equal to or greater than the monthly fee"),
    month: Yup.array()
      .min(1, "Please select at least one month")
      .required("Month selection is required"),
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      amount: 0,
      month: [],
      method: "",
    },
  });

  useEffect(() => {
    if (customer) {
      const calculatedAmount =
        customer?.balance > 0 && customer?.balance < customer?.monthlyFee
          ? customer?.monthlyFee - customer?.balance
          : customer?.balance < 0
            ? customer?.monthlyFee + Math.abs(customer?.balance)
            : customer?.monthlyFee;

      reset({
        amount: calculatedAmount,
        month: customerBillMonth(customer) || [],
      });
    }
  }, [customer, reset]);

  const { error, triggerBkash } = useBkash({
    onSuccess: () => (window.location.href = "/payment/success"),
    onClose: () => (window.location.href = "/"),
    bkashScriptURL:
      "https://scripts.pay.bka.sh/versions/1.2.0-beta/checkout/bKash-checkout.js",
    onCreatePayment: async () => {
      const bill = watch();
      try {
        const payment = {
          amount: Number(bill.amount),
          merchantInvoiceNumber: Date.now(),
          intent: "sale",
          ispOwnerId: customer?.ispOwner?.id,
          name: customer?.name,
          billType: "bill",
          customer: customer?.id,
          user: customer?.reseller
            ? customer?.reseller?.user
            : customer.ispOwner.user,
          userType: customer?.userType,
          medium: bill.method,
          month: bill.month?.map((item) => item.value).join(","),
          paymentStatus: "pending",
          collectedBy: "customer",
          ...(customer?.reseller?.settings?.paymentGateway?.hasPG && {
            resellerId: customer?.reseller?.id,
          }),
        };

        const response = await apiLink.post(`bkash/createPayment`, payment);

        if (response.data?.statusCode === "0000") {
          window.location.href = response.data?.bkashURL;
        }
        return response.data;
      } catch (err) {
        toast.error(err.message);
        return null;
      }
    },
    onExecutePayment: async (paymentID) => {
      const bill = watch();
      const billData = {
        amount: Number(bill.amount),
        name: customer?.name,
        billType: "bill",
        customer: customer?.id,
        ispOwner: customer?.ispOwner.id,
        user: customer?.reseller
          ? customer?.reseller?.user
          : customer.ispOwner.user,
        userType: customer?.userType,
        medium: bill.method,
        paymentStatus: "pending",
        mikrotikPackage: customer?.mikrotikPackage,
        collectorId: customer?.ispOwner.id,
      };
      return await apiLink.post(
        `bkash/executePayment?paymentID=${paymentID}`,
        billData,
      );
    },
  });

  useEffect(() => {
    if (error) {
      localStorage.setItem("paymentError", error?.message);
      window.location.href = "/payment/error";
    }
  }, [error]);

  const triggerSSLCommerz = async () => {
    const bill = watch();
    const data = {
      amount: Number(bill?.amount),
      name: customer.name,
      billType: "bill",
      customer: customer?.id,
      ispOwner: customer.ispOwner.id,
      user: customer?.reseller
        ? customer?.reseller?.user
        : customer.ispOwner.user,
      userType: customer.userType,
      medium: bill.method,
      month: bill.month?.map((item) => item.value).join(","),
      paymentStatus: "pending",
      mikrotikPackage: customer.mikrotikPackage,
      collectorId: customer.ispOwner.id,
    };

    sslCommerzPayment(data);
  };

  let paymentGateway = customer?.reseller?.settings?.paymentGateway?.hasPG
    ? customer?.reseller?.settings?.paymentGateway
    : customer?.ispOwner?.bpSettings?.hasPG
      ? customer?.ispOwner?.bpSettings?.paymentGateway
      : false;

  const paymentMethods = [
    {
      title: "bKashPG",
      logo: BKashLogo,
      trigger: triggerBkash,
      isVisible: paymentGateway?.providers?.bKash,
    },
    {
      title: "sslcommerz",
      logo: SSLCommerzLogo,
      trigger: triggerSSLCommerz,
      isVisible: paymentGateway?.providers?.sslCommerz,
    },
    {
      title: "uddoktapay",
      logo: UddoktaPayLogo,
      trigger: triggerSSLCommerz,
      isVisible: paymentGateway?.providers?.uddoktaPay,
    },
    {
      title: "shurjoPay",
      logo: ShurjoPayLogo,
      trigger: triggerSSLCommerz,
      isVisible: paymentGateway?.providers?.shurjoPay,
    },
  ];

  const formSubmit = () => {};

  return (
    <ComponentCustomModal show={show} setShow={setShow} keyboard centered>
      <form onSubmit={handleSubmit(formSubmit)}>
        <div className="displayGrid">
          <p className="reportCollect shadow-sm bg-white rounded mb-0">
            Billing Information
          </p>

          <div>
            <label className="form-check-label fw-normal">
              <small>Total Bill Amount</small>
              <span className="text-danger"> *</span>
            </label>
            <input
              {...register("amount")}
              className="form-control"
              type="number"
              min={1}
            />
            {errors.amount && (
              <small className="text-danger">{errors.amount.message}</small>
            )}
          </div>

          <div>
            <label className="form-check-label fw-normal">
              <small>Select Bill Month</small>
              <span className="text-danger"> *</span>
            </label>
            <Controller
              name="month"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  className="mt-0 text-dark"
                  options={monthOptions}
                  isMulti
                  placeholder="Select Bill Month"
                  isSearchable
                  value={field.value}
                  onChange={(selectedOptions) =>
                    setValue("month", selectedOptions || [])
                  }
                />
              )}
            />
            {errors.month && (
              <small className="text-danger">{errors.month.message}</small>
            )}
          </div>

          <p className="reportCollect shadow-sm bg-white rounded mb-0">
            Select your payment method
          </p>

          <Controller
            name="method"
            control={control}
            render={({ field }) => (
              <div className="paymentCardDisplayGrid3">
                {paymentMethods?.map(
                  (method) =>
                    method.isVisible && (
                      <button
                        key={method.title}
                        type="submit"
                        className={`payment-card bg-white ${
                          field.value === method.title ? "active" : ""
                        }`}
                        onClick={() => {
                          setValue("method", method.title);
                          method.trigger();
                        }}
                      >
                        <img
                          src={method.logo}
                          alt={method.title}
                          className="payment-logo"
                        />
                      </button>
                    ),
                )}
              </div>
            )}
          />
        </div>
      </form>
    </ComponentCustomModal>
  );
};

export default Payment;
